// src/game-system/skills/skillProgression.js

/**
 * @typedef {Object} SkillRank
 * @property {string} name - Rank name
 * @property {number} modifier - Bonus modifier applied to skill checks
 * @property {string} description - Description of proficiency at this rank
 * @property {string[]} abilities - Special abilities granted at this rank
 */

/**
 * @type {Object.<number, SkillRank>}
 */
export const SKILL_RANKS = {
  0: {
    name: "Untrained",
    modifier: -2,
    description: "You have no training in this skill.",
    abilities: []
  },
  1: {
    name: "Novice",
    modifier: 0,
    description: "You have basic familiarity with this skill.",
    abilities: ["Can attempt basic skill checks without penalty"]
  },
  2: {
    name: "Trained",
    modifier: 2,
    description: "You have received formal training in this skill.",
    abilities: ["Can attempt moderate skill checks", "Can use basic special techniques"]
  },
  3: {
    name: "Expert",
    modifier: 5,
    description: "You have mastered the fundamentals of this skill and developed advanced techniques.",
    abilities: ["Can attempt difficult skill checks", "Can use advanced techniques", "Can train others in basic skills"]
  },
  4: {
    name: "Master",
    modifier: 8,
    description: "You are among the most skilled practitioners in the world.",
    abilities: ["Can attempt nearly impossible skill checks", "Can use master-level techniques", "Can train others to expert level"]
  },
  5: {
    name: "Legendary",
    modifier: 12,
    description: "Your skill is the stuff of legends and your feats seem supernatural.",
    abilities: ["Can attempt impossible skill checks", "Can perform legendary feats", "Can discover new techniques"]
  }
};

/**
 * Calculate skill experience requirements
 * @param {number} rank - Target rank
 * @returns {number} Experience required
 */
export function calculateSkillExpRequirement(rank) {
  if (rank <= 0) return 0;
  if (rank > 5) return -1; // Cannot progress beyond legendary

  // Exponential progression: 100, 400, 900, 1600, 2500
  return rank * rank * 100;
}

/**
 * Get milestone requirements for a skill rank
 * @param {number} rank - Skill rank
 * @returns {Object[]} Array of milestone achievements
 */
export function getSkillRankMilestones(rank) {
  const milestones = {
    1: [
      { description: "Complete 3 basic tasks using this skill", count: 3 },
      { description: "Study the skill fundamentals", count: 1 }
    ],
    2: [
      { description: "Complete 5 moderate tasks using this skill", count: 5 },
      { description: "Train with a skilled mentor", count: 1 },
      { description: "Create something using this skill", count: 1 }
    ],
    3: [
      { description: "Complete 10 difficult tasks using this skill", count: 10 },
      { description: "Develop a unique technique", count: 1 },
      { description: "Overcome a significant challenge", count: 1 }
    ],
    4: [
      { description: "Complete 15 very difficult tasks", count: 15 },
      { description: "Train an apprentice to novice level", count: 1 },
      { description: "Create a masterwork", count: 1 }
    ],
    5: [
      { description: "Complete 20 nearly impossible tasks", count: 20 },
      { description: "Establish a school or technique", count: 1 },
      { description: "Create something of historical significance", count: 1 }
    ]
  };

  return milestones[rank] || [];
}

/**
 * Determine if character is eligible for skill rank up
 * @param {Object} character - Character data
 * @param {string} skillName - Name of the skill
 * @returns {boolean} Whether character can rank up
 */
export function canRankUpSkill(character, skillName) {
  if (!character || !character.skills) return false;

  const characterSkill = character.skills.find(s =>
    s.skill.name.toLowerCase() === skillName.toLowerCase()
  );

  if (!characterSkill) return false;

  const currentRank = characterSkill.level;
  const requiredXP = calculateSkillExpRequirement(currentRank + 1);

  // Can't progress beyond legendary
  if (requiredXP < 0) return false;

  return characterSkill.experience >= requiredXP;
}

/**
 * Rank up a character's skill
 * @param {Object} character - Character data
 * @param {string} skillName - Name of skill to rank up
 * @returns {Object} Updated character data and result message
 */
export function rankUpSkill(character, skillName) {
  if (!character || !character.skills) {
    return {
      character,
      success: false,
      message: "Character data is invalid"
    };
  }

  const skillIndex = character.skills.findIndex(s =>
    s.skill.name.toLowerCase() === skillName.toLowerCase()
  );

  if (skillIndex === -1) {
    return {
      character,
      success: false,
      message: `Character does not have the ${skillName} skill`
    };
  }

  const skill = character.skills[skillIndex];
  const currentRank = skill.level;
  const requiredXP = calculateSkillExpRequirement(currentRank + 1);

  if (requiredXP < 0) {
    return {
      character,
      success: false,
      message: `${skillName} is already at maximum rank`
    };
  }

  if (skill.experience < requiredXP) {
    return {
      character,
      success: false,
      message: `Not enough experience to rank up ${skillName}`
    };
  }

  // Update skill rank
  const updatedSkills = [...character.skills];
  updatedSkills[skillIndex] = {
    ...skill,
    level: currentRank + 1,
    // Keep excess XP for progress toward next rank
    experience: skill.experience - requiredXP
  };

  const newRank = SKILL_RANKS[currentRank + 1];

  return {
    character: {
      ...character,
      skills: updatedSkills
    },
    success: true,
    message: `Congratulations! Your ${skillName} skill is now ${newRank.name}!`,
    abilities: newRank.abilities
  };
}